import { useMutation } from "@tanstack/react-query";
import { Check, Copy, LoaderCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { api, ErroRequisicao } from "../lib/api";

/**
 * Configuração do 2FA em três passos: ler o QR no aplicativo, confirmar um código,
 * guardar os códigos de recuperação (mostrados uma única vez).
 */
export function Configurar2fa({ aoConcluir }: { aoConcluir: () => void }) {
  const [qr, setQr] = useState<string | null>(null);
  const [segredo, setSegredo] = useState("");
  const [codigo, setCodigo] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [codigos, setCodigos] = useState<string[] | null>(null);

  const configurar = useMutation({
    mutationFn: () => api<{ uri: string; segredo: string }>("/auth/2fa/configurar", { method: "POST", json: {} }),
    onSuccess: async (r) => {
      setSegredo(r.segredo);
      // O gerador de QR só é baixado aqui: a entrada (que mostra esta etapa) fica leve para todo mundo.
      const { default: QRCode } = await import("qrcode");
      setQr(await QRCode.toDataURL(r.uri, { margin: 1, width: 220 }));
    },
  });
  const ativar = useMutation({
    mutationFn: (c: string) => api<{ codigos: string[] }>("/auth/2fa/ativar", { method: "POST", json: { codigo: c } }),
    onSuccess: (r) => setCodigos(r.codigos),
    onError: (e) => setErro(e instanceof ErroRequisicao ? e.corpo.mensagem : "Não foi possível ativar"),
  });

  // Gera o segredo uma vez ao abrir.
  const { mutate } = configurar;
  useEffect(() => {
    mutate();
  }, [mutate]);

  if (codigos) return <CodigosRecuperacao codigos={codigos} aoConcluir={aoConcluir} />;

  return (
    <div className="space-y-4">
      <ol className="list-decimal space-y-1 pl-5 text-sm text-texto-suave">
        <li>Instale um aplicativo autenticador (Google Authenticator, Microsoft Authenticator, Authy…).</li>
        <li>No aplicativo, adicione uma conta lendo o QR code abaixo.</li>
        <li>Digite o código de 6 dígitos que aparece no aplicativo.</li>
      </ol>

      <div className="flex flex-col items-center gap-2 rounded-2xl bg-white p-4">
        {qr ? <img src={qr} alt="QR code para o aplicativo autenticador" className="size-[220px]" /> : <LoaderCircle className="size-8 animate-spin text-texto-fraco" />}
      </div>
      {segredo && (
        <details className="text-sm">
          <summary className="cursor-pointer text-texto-suave">Não consigo ler o QR code</summary>
          <p className="mt-2">Digite esta chave no aplicativo:</p>
          <code className="mt-1 block rounded-xl bg-superficie-2 px-3 py-2 font-mono tracking-wider break-all select-all">
            {segredo.match(/.{1,4}/g)?.join(" ")}
          </code>
        </details>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErro(null);
          ativar.mutate(codigo);
        }}
        className="space-y-2"
      >
        <label htmlFor="codigo-ativar" className="block text-sm font-medium">
          Código do aplicativo
        </label>
        <input
          id="codigo-ativar"
          className="campo text-center font-mono text-xl tracking-[0.4em]"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={codigo}
          onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ""))}
          aria-invalid={Boolean(erro)}
        />
        {erro && <p className="text-[13px] text-perigo">{erro}</p>}
        <button type="submit" className="botao botao-primario w-full" disabled={codigo.length !== 6 || ativar.isPending || !qr}>
          {ativar.isPending && <LoaderCircle className="size-4 animate-spin" />}
          Ativar 2FA
        </button>
      </form>
    </div>
  );
}

export function CodigosRecuperacao({ codigos, aoConcluir }: { codigos: string[]; aoConcluir: () => void }) {
  const [copiado, setCopiado] = useState(false);
  const [guardou, setGuardou] = useState(false);
  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-aviso-suave p-3 text-sm text-aviso">
        <p className="font-semibold">Guarde estes códigos de recuperação</p>
        <p>Cada um vale uma vez, se você perder o celular. Eles não serão mostrados de novo.</p>
      </div>
      <ul className="grid grid-cols-2 gap-2 rounded-2xl bg-superficie-2 p-3 font-mono text-[15px]">
        {codigos.map((c) => (
          <li key={c} className="text-center select-all">
            {c}
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="botao botao-secundario w-full"
        onClick={async () => {
          await navigator.clipboard?.writeText(codigos.join("\n"));
          setCopiado(true);
        }}
      >
        {copiado ? <Check className="size-4" /> : <Copy className="size-4" />}
        {copiado ? "Copiados" : "Copiar códigos"}
      </button>
      <label className="flex items-center gap-3 text-sm">
        <input type="checkbox" className="size-5 accent-[var(--marca)]" checked={guardou} onChange={(e) => setGuardou(e.target.checked)} />
        Guardei os códigos em lugar seguro
      </label>
      <button type="button" className="botao botao-primario w-full" disabled={!guardou} onClick={aoConcluir}>
        Concluir
      </button>
    </div>
  );
}
